/**
 * ============================================================================
 * USE AUTH HOOK
 * ============================================================================
 * 
 * Custom hook for authentication operations
 */

import { useState, useCallback } from 'react';
import * as authService from '../services/auth';
import useAuthStore from '../store/authStore';

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setAuth, logout: logoutStore, user, isAuthenticated } = useAuthStore();

  /**
   * Signup with phone number
   * @param {Object|string} payload - { phone, fullName?, countryCode? } or phone string
   */
  const signupWithPhone = useCallback(async (payload) => {
    try {
      setLoading(true);
      setError(null);
      const data = typeof payload === 'object' && payload !== null && 'phone' in payload
        ? payload
        : { phone: payload };
      const response = await authService.signupWithPhone(data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Signup failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify signup OTP
   */
  const verifySignupOTP = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      // Handle both object and separate params
      const payload = typeof data === 'object' ? data : { phone: data, otp: arguments[1] };
      const response = await authService.verifySignupOTP(payload);
      const payloadData = response?.data ?? response;
      const user = payloadData?.data?.user ?? payloadData?.user;
      const tokens = payloadData?.data?.tokens ?? payloadData?.tokens;
      if (user && tokens) {
        setAuth(user, tokens);
      }
      return response?.data ?? response;
    } catch (err) {
      setError(err.message || 'OTP verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setAuth]);

  /**
   * Login with phone number
   */
  const loginWithPhone = useCallback(async (phone) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.loginWithPhone({ phone });
      return response.data;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify login OTP
   */
  const verifyLoginOTP = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      // Handle both object and separate params
      const payload = typeof data === 'object' ? data : { phone: data, otp: arguments[1] };
      const response = await authService.verifyLoginOTP(payload);
      const payloadData = response?.data ?? response;
      const user = payloadData?.data?.user ?? payloadData?.user;
      const tokens = payloadData?.data?.tokens ?? payloadData?.tokens;
      if (user && tokens) {
        setAuth(user, tokens);
      }
      return response?.data ?? response;
    } catch (err) {
      setError(err.message || 'Login verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setAuth]);

  /**
   * Refresh access token
   */
  const refreshToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.refreshToken();
      
      // Update tokens
      if (response.data?.tokens) {
        useAuthStore.getState().setTokens(response.data.tokens);
      }
      
      return response.data;
    } catch (err) {
      setError(err.message || 'Token refresh failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await authService.logout();
      logoutStore();
    } catch (err) {
      setError(err.message || 'Logout failed');
      // Still logout locally even if API call fails
      logoutStore();
    } finally {
      setLoading(false);
    }
  }, [logoutStore]);

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (phone) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.requestPasswordReset({ phone });
      return response.data;
    } catch (err) {
      setError(err.message || 'Password reset request failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Confirm password reset
   */
  const confirmPasswordReset = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      // Handle both object and separate params
      const payload = typeof data === 'object' 
        ? data 
        : { phone: data, code: arguments[1], newPassword: arguments[2], confirmPassword: arguments[3] };
      const response = await authService.confirmPasswordReset(payload);
      return response.data;
    } catch (err) {
      setError(err.message || 'Password reset failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Resend OTP
   */
  const resendOTP = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      // Handle both object and separate params
      const payload = typeof data === 'object' ? data : { phone: data, purpose: arguments[1] };
      const response = await authService.resendOTP(payload);
      return response.data;
    } catch (err) {
      setError(err.message || 'OTP resend failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get current user
   */
  const getCurrentUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.getCurrentUser();
      
      // Update user in store
      if (response.data?.user) {
        useAuthStore.getState().setUser(response.data.user);
      }
      
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get current user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add email to account
   * @param {Object} payload - { email: string }
   */
  const addEmail = useCallback(async (payload) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.addEmail(payload);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to add email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify email with OTP
   * @param {Object} payload - { code: string } (6-digit OTP)
   */
  const verifyEmail = useCallback(async (payload) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.verifyEmail(payload);
      return response.data;
    } catch (err) {
      setError(err.message || 'Email verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Set password
   */
  const setPassword = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      // Handle both object and separate params
      const payload = typeof data === 'object' 
        ? data 
        : { password: data, confirmPassword: arguments[1] };
      const response = await authService.setPassword(payload);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to set password');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    user,
    isAuthenticated,
    
    // Actions
    signupWithPhone,
    verifySignupOTP,
    loginWithPhone,
    verifyLoginOTP,
    refreshToken,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    resendOTP,
    getCurrentUser,
    addEmail,
    verifyEmail,
    setPassword,
  };
};

export default useAuth;
